import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Grid,
  useTheme,
} from '@mui/material';
import {
  Dashboard as OverviewIcon,
  Psychology as AIIcon,
  Star as GoldenIcon,
  People as UsersIcon,
  Campaign as AdvertisersIcon,
  CloudDownload as ScraperIcon,
  Language as DomainsIcon,
  Chat as SlackIcon,
  BugReport as DiagnosticsIcon,
  VpnKey as APITokensIcon,
  Label as RuleLabelsIcon,
  Timer as SessionsIcon,
  Settings as SettingsIcon,
  Webhook as WebhooksIcon,
  Work as JobsIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { UsersTab } from '@/features/admin-console';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// Stat Card Component for Overview
interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

const StatCard = ({ title, value, icon, color, subtitle }: StatCardProps) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        borderRadius: '10px',
        boxShadow: '0 10px 30px rgba(146,153,184,0.15)',
        border: 'none',
        height: '100%',
      }}
    >
      <CardContent sx={{ padding: '20px !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '13px',
                fontWeight: 500,
                mb: 1,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 0.5,
              }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '12px',
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '10px',
              backgroundColor: `${color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const AdminConsolePage = () => {
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const tabs = [
    { label: 'Overview', icon: <OverviewIcon sx={{ fontSize: 18 }} /> },
    { label: 'AI Contexts', icon: <AIIcon sx={{ fontSize: 18 }} /> },
    { label: 'Golden Contexts', icon: <GoldenIcon sx={{ fontSize: 18 }} /> },
    { label: 'Users', icon: <UsersIcon sx={{ fontSize: 18 }} /> },
    { label: 'Advertisers', icon: <AdvertisersIcon sx={{ fontSize: 18 }} /> },
    { label: 'Scraper', icon: <ScraperIcon sx={{ fontSize: 18 }} /> },
    { label: 'Domains', icon: <DomainsIcon sx={{ fontSize: 18 }} /> },
    { label: 'Slack', icon: <SlackIcon sx={{ fontSize: 18 }} /> },
    { label: 'Diagnostics', icon: <DiagnosticsIcon sx={{ fontSize: 18 }} /> },
    { label: 'API Tokens', icon: <APITokensIcon sx={{ fontSize: 18 }} /> },
    { label: 'Rule Labels', icon: <RuleLabelsIcon sx={{ fontSize: 18 }} /> },
    { label: 'Sessions', icon: <SessionsIcon sx={{ fontSize: 18 }} /> },
    { label: 'Settings', icon: <SettingsIcon sx={{ fontSize: 18 }} /> },
    { label: 'Webhooks', icon: <WebhooksIcon sx={{ fontSize: 18 }} /> },
    { label: 'Jobs', icon: <JobsIcon sx={{ fontSize: 18 }} /> },
    { label: 'Email Templates', icon: <EmailIcon sx={{ fontSize: 18 }} /> },
  ];

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Admin Console
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage system settings, users, and configuration
        </Typography>
      </Box>

      <Card
        sx={{
          borderRadius: '10px',
          boxShadow: '0 10px 30px rgba(146,153,184,0.15)',
          border: 'none',
        }}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 3,
            pt: 2,
          }}
        >
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minHeight: 48,
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 500,
                color: theme.palette.text.secondary,
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.primary.main,
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>

        <CardContent sx={{ padding: '25px !important' }}>
          {/* Overview Tab */}
          <TabPanel value={currentTab} index={0}>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 3, color: theme.palette.text.primary }}
              >
                System Overview
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} lg={3}>
                  <StatCard
                    title="Total Users"
                    value="47"
                    subtitle="Active accounts"
                    icon={<UsersIcon sx={{ fontSize: 24 }} />}
                    color={theme.palette.primary.main}
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <StatCard
                    title="Active Advertisers"
                    value="12"
                    subtitle="Running campaigns"
                    icon={<AdvertisersIcon sx={{ fontSize: 24 }} />}
                    color={theme.palette.secondary.main}
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <StatCard
                    title="API Requests"
                    value="2,847"
                    subtitle="Last 24 hours"
                    icon={<APITokensIcon sx={{ fontSize: 24 }} />}
                    color={theme.palette.success.main}
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <StatCard
                    title="Active Jobs"
                    value="5"
                    subtitle="In queue"
                    icon={<JobsIcon sx={{ fontSize: 24 }} />}
                    color={theme.palette.info.main}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 4 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, mb: 3, color: theme.palette.text.primary }}
                >
                  System Status
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        borderRadius: '10px',
                        boxShadow: '0 5px 15px rgba(146,153,184,0.1)',
                        border: 'none',
                      }}
                    >
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                          Services
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              API Server
                            </Typography>
                            <Box
                              sx={{
                                px: 2,
                                py: 0.5,
                                borderRadius: '12px',
                                backgroundColor: `${theme.palette.success.main}15`,
                                color: theme.palette.success.main,
                                fontSize: '12px',
                                fontWeight: 600,
                              }}
                            >
                              Operational
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              Database
                            </Typography>
                            <Box
                              sx={{
                                px: 2,
                                py: 0.5,
                                borderRadius: '12px',
                                backgroundColor: `${theme.palette.success.main}15`,
                                color: theme.palette.success.main,
                                fontSize: '12px',
                                fontWeight: 600,
                              }}
                            >
                              Operational
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              Scraper Service
                            </Typography>
                            <Box
                              sx={{
                                px: 2,
                                py: 0.5,
                                borderRadius: '12px',
                                backgroundColor: `${theme.palette.success.main}15`,
                                color: theme.palette.success.main,
                                fontSize: '12px',
                                fontWeight: 600,
                              }}
                            >
                              Operational
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              Slack Integration
                            </Typography>
                            <Box
                              sx={{
                                px: 2,
                                py: 0.5,
                                borderRadius: '12px',
                                backgroundColor: `${theme.palette.success.main}15`,
                                color: theme.palette.success.main,
                                fontSize: '12px',
                                fontWeight: 600,
                              }}
                            >
                              Operational
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        borderRadius: '10px',
                        boxShadow: '0 5px 15px rgba(146,153,184,0.1)',
                        border: 'none',
                      }}
                    >
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                          Recent Activity
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              User login
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              admin@example.com • 5 minutes ago
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Campaign created
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              New RSOC campaign • 12 minutes ago
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Rule updated
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Performance rule modified • 28 minutes ago
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Scraper completed
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Domain scan finished • 1 hour ago
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </TabPanel>

          {/* AI Contexts Tab */}
          <TabPanel value={currentTab} index={1}>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Box sx={{ mb: 2 }}>{tabs[1].icon}</Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {tabs[1].label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This section is under development
              </Typography>
            </Box>
          </TabPanel>

          {/* Golden Contexts Tab */}
          <TabPanel value={currentTab} index={2}>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Box sx={{ mb: 2 }}>{tabs[2].icon}</Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {tabs[2].label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This section is under development
              </Typography>
            </Box>
          </TabPanel>

          {/* Users Tab */}
          <TabPanel value={currentTab} index={3}>
            <UsersTab />
          </TabPanel>

          {/* Placeholder tabs for remaining sections */}
          {tabs.slice(4).map((tab, index) => (
            <TabPanel key={index + 4} value={currentTab} index={index + 4}>
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Box sx={{ mb: 2 }}>{tab.icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {tab.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This section is under development
                </Typography>
              </Box>
            </TabPanel>
          ))}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminConsolePage;
